import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { usePasswordResetConfirm } from '@/lib/auth';
import {
  InvalidTokenError,
  MultiUserDisabledError,
  RateLimitedError,
} from '@/lib/api';

/**
 * Confirma un password reset usando un token leído de `?token=...`.
 *
 * Diseño:
 * - Si falta token en la URL → mostramos un mensaje y enlace al request form.
 *   No bloqueamos el render con un throw para no romper el routing.
 * - Mostramos confirm password client-side para evitar typos.
 * - Al éxito, el backend establece la nueva sesión y `useAuthStatus` se
 *   invalida → el router redirige a `/` automáticamente.
 */
export function PasswordResetConfirmForm(): ReactElement {
  // `from: '__root__'` permite leer el search global sin estar montado en una
  // ruta tipada concreta — necesario porque la ruta usa `validateSearch` con
  // `token` opcional para no romper si llega sin token.
  const search = useSearch({ strict: false }) as { token?: string };
  const token = typeof search.token === 'string' ? search.token : '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const mutation = usePasswordResetConfirm();

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (mutation.isPending) return;
    if (!token) {
      setLocalError('Token no encontrado en la URL.');
      return;
    }
    if (password.length < 8) {
      setLocalError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (password !== confirm) {
      setLocalError('Las contraseñas no coinciden.');
      return;
    }
    setLocalError(null);
    mutation.mutate({ token, newPassword: password });
  }

  function topError(): string | null {
    if (localError) return localError;
    const err = mutation.error;
    if (!err) return null;
    if (err instanceof InvalidTokenError) {
      return 'El enlace ha caducado o no es válido. Solicita uno nuevo.';
    }
    if (err instanceof MultiUserDisabledError) {
      return 'Esta instalación no permite reset por email.';
    }
    if (err instanceof RateLimitedError) {
      const seconds = err.retryAfterSeconds;
      if (seconds === null) return 'Demasiados intentos. Espera un poco.';
      const minutes = Math.max(1, Math.ceil(seconds / 60));
      return `Demasiados intentos. Espera ${minutes} minuto${minutes === 1 ? '' : 's'}.`;
    }
    return 'No se pudo cambiar la contraseña.';
  }

  const err = topError();

  if (!token) {
    return (
      <div className="space-y-5">
        <header>
          <h2 className="text-xl font-semibold text-slate-900">Cambiar contraseña</h2>
        </header>
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          Falta el token en la URL. Solicita un nuevo enlace de recuperación.
        </p>
        <Link
          to="/password-reset/request"
          className="text-sm text-teal-700 underline-offset-2 hover:underline focus:outline-none focus:ring-2 focus:ring-teal-600"
        >
          Solicitar enlace
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-slate-900">Cambiar contraseña</h2>
        <p className="mt-1 text-sm text-slate-600">Introduce tu nueva contraseña.</p>
      </header>

      <div>
        <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700">
          Nueva contraseña
        </label>
        <input
          id="newPassword"
          name="newPassword"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={mutation.isPending}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
        <p className="mt-1 text-xs text-slate-500">Mínimo 8 caracteres.</p>
      </div>

      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700">
          Confirmar contraseña
        </label>
        <input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={mutation.isPending}
          className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm focus:border-teal-600 focus:outline-none focus:ring-1 focus:ring-teal-600 disabled:cursor-not-allowed disabled:bg-slate-100"
        />
      </div>

      {err && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {err}
        </p>
      )}

      {mutation.isSuccess && (
        <p role="status" className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          Contraseña cambiada. Redirigiendo…
        </p>
      )}

      <button
        type="submit"
        disabled={
          mutation.isPending || password.length === 0 || confirm.length === 0
        }
        aria-busy={mutation.isPending}
        className="w-full rounded-md bg-teal-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {mutation.isPending ? 'Guardando…' : 'Cambiar contraseña'}
      </button>
    </form>
  );
}
